import {ConfirmationDialog, dateTime, DetailPane, FormDialog} from '@management-ui/core';
import {Button, styled} from '@mui/material';
import React, {useCallback, useContext, useMemo, useState} from 'react';
import withTaskStatuses from '../withTaskStatuses';
import {ServiceContext} from '../Services';
import UserSelectionForm from '../../modules/admin/forms/UserSelectionForm';
import DateForm from '../../modules/admin/forms/DateForm';
import moment from 'moment';
import {useHistory} from 'react-router-dom';
import {reverse} from 'named-urls';
import routes from '../../routes';
import {taskNextOccurrence, taskStatus} from '../../utilities';
import TaskForm from '../../modules/admin/forms/TaskForm';
import {useTheme} from '@mui/styles';
import Task from './Task';
import {useSelector} from 'react-redux';

const Actions = styled('div')(({theme}) => ({
  display: 'flex',
  flexWrap: 'wrap',
  margin: theme.spacing(-1, 0, 0),

  '& .MuiButton-root': {
    color: theme.palette.common.white,
    margin: theme.spacing(0.5, 1, 0.5, 0)
  }
}));

const Admin = ({task, onTaskUpdated, onChangeTask, onLoading, statuses}) => {
  const services = useContext(ServiceContext);
  const theme = useTheme();
  const history = useHistory();
  const user = useSelector(state => state['auth'].user);
  const userCanManage = useMemo(() => user.permissions.can_manage_tasks, [user]);
  const [showComplete, setShowComplete] = useState(false);
  const [showCompleteRecurring, setShowCompleteRecurring] = useState(false);
  const [showRevert, setShowRevert] = useState(false);
  const [showRevertOccurrences, setShowRevertOccurrences] = useState(false);
  const [showReassign, setShowReassign] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  const handleToggleComplete = useCallback((confirmed) => {
    setShowComplete(false);
    if (confirmed) {
      onLoading(true);
      services.task.completeTask(task).then((toggled) => {
        onLoading(false);
        onTaskUpdated(toggled);
      }).catch(() => onLoading(false));
    }
  }, [task, services, onTaskUpdated, onLoading]);

  const handleCompleteRecurring = useCallback(async ({date}) => {
    onLoading(true);
    return services.task.completeTask(task, date).then((next) => {
      onLoading(false);
      setShowCompleteRecurring(false);
      history.push(reverse(routes.tasks.detail, {id: next.id}));
    }).catch(() => onLoading(false));
  }, [task, onLoading, services, history]);

  const handleRevert = useCallback((confirmed) => {
    setShowRevert(false);
    if (confirmed) {
      if (task.future_occurrences?.length > 0) {
        setShowRevertOccurrences(true);
      } else {
        onLoading(true);
        services.task.revertTask(task).then((reverted) => {
          onLoading(false);
          onTaskUpdated(reverted);
        }).catch(() => onLoading(false));
      }
    }
  }, [task, services, onTaskUpdated, onLoading]);

  const handleRevertOccurrences = useCallback((confirmed) => {
    setShowRevertOccurrences(false);
    onLoading(true);
    services.task.revertTask(task, confirmed).then((reverted) => {
      onLoading(false);
      onTaskUpdated(reverted);
    }).catch(() => onLoading(false));
  }, [task, services, onTaskUpdated, onLoading]);

  const handleReassign = useCallback(({user_id}) => {
    return services.task.updateTask(task, {assignee_id: user_id}).then((saved) => {
      onTaskUpdated(saved);
      setShowReassign(false);
    }).catch(() => null);
  }, [task, onTaskUpdated, services]);

  const handleReschedule = useCallback(({date}) => {
    return services.task.updateTask(task, {deadline: moment(date).format('YYYY-MM-DD')}).then((saved) => {
      onTaskUpdated(saved);
      setShowReschedule(false);
    }).catch(() => null);
  }, [task, onTaskUpdated, services]);

  const handleEdited = useCallback((updated) => {
    onTaskUpdated(updated);
    setShowEdit(false);
  }, [onTaskUpdated]);

  const actions = useMemo(() => {
    const getColour = (status) => statuses.find(({key}) => key === status).colour;
    const actions = [];
    if (!task.archived) {
      if (taskStatus(task) !== 'completed') {
        actions.push({
          title: 'Edit',
          colour: theme['palette']['taskStatus'].update.main,
          onClick: () => setShowEdit(true)
        });
        if (userCanManage) {
          actions.push({title: 'Re-assign', colour: getColour('overdue'), onClick: () => setShowReassign(true)});
        }
        actions.push({title: 'Reschedule', colour: getColour('pending'), onClick: () => setShowReschedule(true)});
        actions.push({
          title: 'Mark as Complete', colour: getColour('completed'), onClick: () => {
            if (task.schedule) {
              setShowCompleteRecurring(true);
            } else {
              setShowComplete(true);
            }
          }
        });
      } else {
        actions.push({
          title: 'Un-complete Task', colour: theme['palette']['taskStatus'].revert.main, onClick: () => {
            setShowRevert(true);
          }
        });
      }
    }
    return actions;
  }, [task, statuses, theme, userCanManage]);

  return (
    <DetailPane
      title="Admin Details"
      details={useMemo(() => [
        {title: 'Created', value: dateTime(task.created_at)},
        {title: 'Last Updated', value: dateTime(task.updated_at)},
        ...(task.schedule ? [
          {
            title: 'Next Occurrence',
            value: task.future_occurrences?.length > 0 ?
              <Task task={task.future_occurrences[0]} onSelect={onChangeTask} showDeadline={true}/> : '-'
          },
          {
            title: 'Previous Occurrence',
            value: task.previous_occurrence ?
              <Task task={task.previous_occurrence} onSelect={onChangeTask} showCompleted={true}/> : '-'
          },
        ] : []),
        ...(actions.length > 0 ? [{
          title: 'Actions', value: (
            <Actions>
              {actions.map(({title, colour, onClick, disabled = false}, index) => (
                <Button
                  key={index}
                  size="small"
                  variant="contained"
                  style={disabled ? {} : {backgroundColor: colour}}
                  onClick={onClick}
                  disabled={disabled}
                >
                  {title}
                </Button>
              ))}
            </Actions>
          )
        }] : [])
      ], [task, actions, onChangeTask])}
      dialogs={[
        (props) => (
          <FormDialog
            {...props}
            title="Edit Task"
            open={showEdit}
            onClose={() => setShowEdit(false)}
            render={(props) => (
              <TaskForm
                {...props}
                task={task}
                onSaved={handleEdited}/>
            )}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showComplete}
            title="Mark as Complete"
            message="Are you sure you want to mark this task as complete?"
            onClose={handleToggleComplete}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showRevert}
            title="Revert Task"
            message="Are you sure you want to un-complete this task?"
            onClose={handleRevert}
          />
        ),
        (props) => (
          <ConfirmationDialog
            {...props}
            open={showRevertOccurrences}
            title="Remove Future Occurrence(s)"
            message="Would you like to remove the future occurrence(s) of this task which have already been created?"
            onClose={handleRevertOccurrences}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showCompleteRecurring}
            title="Select Next Occurrence"
            maxWidth="sm"
            onClose={() => setShowCompleteRecurring(false)}
            render={(props) => (
              <DateForm
                {...props}
                entity={{date: taskNextOccurrence(task)}}
                onSave={(data) => handleCompleteRecurring(data)}/>
            )}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showReassign}
            title="Re-assign Task"
            maxWidth="sm"
            onClose={() => setShowReassign(false)}
            render={(props) => (
              <UserSelectionForm
                {...props}
                entity={{user_id: task.assignee ? task.assignee.id : null}}
                onSave={handleReassign}/>
            )}
          />
        ),
        (props) => (
          <FormDialog
            {...props}
            open={showReschedule}
            title="Change Deadline"
            maxWidth="sm"
            onClose={() => setShowReschedule(false)}
            render={(props) => (
              <DateForm
                {...props}
                entity={{date: task.deadline}}
                onSave={(data) => handleReschedule(data)}/>
            )}
          />
        ),
      ]}
    />
  );
};

export default withTaskStatuses(Admin);
